import React, { useRef, useState } from 'react';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import uploadSmallTokens from '@amzn/meridian-tokens/base/icon/upload-small';
import stopTokens from '@amzn/meridian-tokens/base/icon/stop';
import pauseTokens from '@amzn/meridian-tokens/base/icon/pause';
import playTokens from '@amzn/meridian-tokens/base/icon/play';
import soundOnTokens from '@amzn/meridian-tokens/base/icon/sound-on';
import soundOffTokens from '@amzn/meridian-tokens/base/icon/sound-off';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import store from '../../../store/store';
import {
  startFileStreaming,
  pauseFileStreaming,
  resumeFileStreaming,
  stopFileStreaming,
  updateFileStreamingProgress,
  disableNoteRecording,
  enableNoteRecording,
} from '../../../store/recordingSlice';
import { fetchSession } from '../../../store/sessionSlice';
import { fetchTranscript } from '../../../store/transcriptSlice';
import { showSuccess, showError } from '../../../store/toastsSlice';
import { websocketService } from '../../../services/websocketService';
import { LANGUAGE_OPTIONS_BILINGUAL_EN_ES } from '../../../constants';
import { TEXT_COLOR_SECONDARY } from '../cssConstants';
import { v4 as uuidv4 } from 'uuid';

interface AudioFileUploadProps {
  size?: 'small' | 'medium' | 'large';
  useCaseType?: string;
  bilingualMode?: boolean;
  siteCode?: string;
  disabled?: boolean;
  // Fired right before streaming starts (after a valid file is selected).
  // Mirrors RecordingButton so the resume flow can upgrade field config and
  // close the "Identify Your Role" panel.
  onBeforeStart?: () => void;
}

const CHUNK_SIZE_SAMPLES = 4096;
const BYTES_PER_SAMPLE = 2;
const CHUNK_SIZE_BYTES = CHUNK_SIZE_SAMPLES * BYTES_PER_SAMPLE;

// Module-level state — survives component remount during /session/new -> /session/{id} redirect
let modulePaused = false;
let moduleDelay: { wait: (ms: number) => Promise<void>; cancel: () => void } | null = null;
let moduleAudioContext: AudioContext | null = null;
let moduleAudioSource: AudioBufferSourceNode | null = null;
let modulePlayAudio = true;
// Stored for resume
let modulePcmData: ArrayBuffer | null = null;
let moduleSampleRate = 16000;
let moduleOffset = 0;
let moduleTotalBytes = 0;
let moduleUseCaseType: string | undefined = undefined;
let moduleLanguageCode: string | undefined = undefined;
let moduleGroupId: string | undefined = undefined;
let moduleStreamingPromise: Promise<void> | null = null;

interface WavHeader {
  sampleRate: number;
  channels: number;
  bitsPerSample: number;
  dataOffset: number;
}

function parseWavHeader(buffer: ArrayBuffer): WavHeader | null {
  const view = new DataView(buffer);
  const riff = String.fromCharCode(view.getUint8(0), view.getUint8(1), view.getUint8(2), view.getUint8(3));
  if (riff !== 'RIFF') return null;

  const wave = String.fromCharCode(view.getUint8(8), view.getUint8(9), view.getUint8(10), view.getUint8(11));
  if (wave !== 'WAVE') return null;

  let offset = 12;
  let sampleRate = 16000;
  let channels = 1;
  let bitsPerSample = 16;
  let dataOffset = 44;

  while (offset < buffer.byteLength - 8) {
    const chunkId = String.fromCharCode(
      view.getUint8(offset), view.getUint8(offset + 1),
      view.getUint8(offset + 2), view.getUint8(offset + 3)
    );
    const chunkSize = view.getUint32(offset + 4, true);

    if (chunkId === 'fmt ') {
      channels = view.getUint16(offset + 10, true);
      sampleRate = view.getUint32(offset + 12, true);
      bitsPerSample = view.getUint16(offset + 22, true);
    } else if (chunkId === 'data') {
      dataOffset = offset + 8;
      break;
    }

    offset += 8 + chunkSize;
  }

  return { sampleRate, channels, bitsPerSample, dataOffset };
}

function createInterruptibleDelay(): {
  wait: (ms: number) => Promise<void>;
  cancel: () => void;
} {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let resolveFn: (() => void) | null = null;

  return {
    wait(ms: number) {
      return new Promise<void>((resolve) => {
        resolveFn = resolve;
        timeoutId = setTimeout(() => {
          timeoutId = null;
          resolveFn = null;
          resolve();
        }, ms);
      });
    },
    cancel() {
      if (timeoutId !== null) clearTimeout(timeoutId);
      if (resolveFn) resolveFn();
      resolveFn = null;
    },
  };
}

function stopModulePlayback() {
  if (moduleAudioSource) {
    try { moduleAudioSource.stop(); } catch (_) {}
    moduleAudioSource = null;
  }
  if (moduleAudioContext) {
    moduleAudioContext.close().catch(() => {});
    moduleAudioContext = null;
  }
}

function startPlayback(pcmData: ArrayBuffer, sampleRate: number, startOffset: number) {
  try {
    const ctx = new AudioContext({ sampleRate });
    moduleAudioContext = ctx;

    const remainingData = pcmData.slice(startOffset);
    const int16 = new Int16Array(remainingData);
    const float32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) {
      float32[i] = int16[i] / 32768;
    }

    const audioBuffer = ctx.createBuffer(1, float32.length, sampleRate);
    audioBuffer.getChannelData(0).set(float32);

    const source = ctx.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(ctx.destination);
    source.start();
    moduleAudioSource = source;

    source.onended = () => {
      moduleAudioSource = null;
    };
  } catch (error) {
    console.error('Failed to play audio:', error);
  }
}

function handlePauseStreaming() {
  modulePaused = true;
  if (moduleDelay) moduleDelay.cancel();
  stopModulePlayback();
  websocketService.pause();
  store.dispatch(pauseFileStreaming());
}

function handleResumeStreaming() {
  if (!modulePcmData || moduleOffset >= moduleTotalBytes) return;
  modulePaused = false;
  store.dispatch(resumeFileStreaming());

  const state = store.getState();
  const existingSessionId = state.session.currentSession?.session_id;
  websocketService.startRecording(existingSessionId, 'audio/pcm', moduleSampleRate, moduleUseCaseType, moduleLanguageCode, moduleGroupId);

  if (modulePlayAudio && modulePcmData) {
    startPlayback(modulePcmData, moduleSampleRate, moduleOffset);
  }

  const chunkIntervalMs = (CHUNK_SIZE_SAMPLES / moduleSampleRate) * 1000;
  const delay = createInterruptibleDelay();
  moduleDelay = delay;

  (async () => {
    while (moduleOffset < moduleTotalBytes && !modulePaused) {
      const end = Math.min(moduleOffset + CHUNK_SIZE_BYTES, moduleTotalBytes);
      const chunk = modulePcmData!.slice(moduleOffset, end);
      websocketService.sendAudioData(chunk);
      moduleOffset = end;
      store.dispatch(updateFileStreamingProgress(Math.round((moduleOffset / moduleTotalBytes) * 100)));
      await delay.wait(chunkIntervalMs);
    }
    moduleDelay = null;

    if (!modulePaused) {
      websocketService.stop();
      const sessionId = websocketService.getSessionId() || store.getState().session.currentSession?.session_id;
      if (sessionId) {
        store.dispatch(fetchSession(sessionId) as any);
        store.dispatch(fetchTranscript(sessionId) as any);
      }
      store.dispatch(showSuccess('Audio file streaming complete'));
      store.dispatch(stopFileStreaming());
      store.dispatch(enableNoteRecording());
      modulePcmData = null;
      moduleOffset = 0;
      moduleTotalBytes = 0;
    }
  })();
}

async function streamAudioFileFromOffset() {
  const dispatch = store.dispatch;

  try {
    // Reconnect WebSocket for the resumed segment
    websocketService.initialize(dispatch);

    const connectionId = `conn-file-${uuidv4()}`;
    const state = store.getState();
    const existingSessionId = state.session.currentSession?.session_id;
    await websocketService.connect(connectionId, existingSessionId);
    websocketService.startRecording(existingSessionId, 'audio/pcm', moduleSampleRate, moduleUseCaseType, moduleLanguageCode, moduleGroupId);

    if (modulePlayAudio && modulePcmData) {
      startPlayback(modulePcmData, moduleSampleRate, moduleOffset);
    }

    const chunkIntervalMs = (CHUNK_SIZE_SAMPLES / moduleSampleRate) * 1000;
    const delay = createInterruptibleDelay();
    moduleDelay = delay;

    while (moduleOffset < moduleTotalBytes && !modulePaused) {
      const end = Math.min(moduleOffset + CHUNK_SIZE_BYTES, moduleTotalBytes);
      const chunk = modulePcmData!.slice(moduleOffset, end);
      websocketService.sendAudioData(chunk);
      moduleOffset = end;
      dispatch(updateFileStreamingProgress(Math.round((moduleOffset / moduleTotalBytes) * 100)));

      await delay.wait(chunkIntervalMs);
    }

    moduleDelay = null;

    // Only finalize if we finished (not paused)
    if (!modulePaused) {
      websocketService.stop();

      const sessionId = websocketService.getSessionId() || store.getState().session.currentSession?.session_id;
      if (sessionId) {
        dispatch(fetchSession(sessionId) as any);
        dispatch(fetchTranscript(sessionId) as any);
      }

      dispatch(showSuccess('Audio file streaming complete'));
      dispatch(stopFileStreaming());
      dispatch(enableNoteRecording());
      modulePcmData = null;
      moduleOffset = 0;
      moduleTotalBytes = 0;
    }
    // If paused, the WebSocket stays open — resume will send start_recording again
  } catch (error: any) {
    console.error('Failed to stream audio file:', error);
    dispatch(showError(error.message || 'Failed to stream audio file'));
    stopModulePlayback();
    dispatch(stopFileStreaming());
    dispatch(enableNoteRecording());
    modulePcmData = null;
    moduleOffset = 0;
    moduleTotalBytes = 0;
  }
}

async function startStreaming(pcmData: ArrayBuffer, sampleRate: number, useCaseType?: string, languageCode?: string, groupId?: string) {
  modulePaused = false;
  modulePcmData = pcmData;
  moduleSampleRate = sampleRate;
  moduleOffset = 0;
  moduleTotalBytes = pcmData.byteLength;
  moduleUseCaseType = useCaseType;
  moduleLanguageCode = languageCode;
  moduleGroupId = groupId;

  const dispatch = store.dispatch;
  dispatch(startFileStreaming());
  dispatch(disableNoteRecording());
  dispatch(showSuccess('Streaming audio file...'));

  moduleStreamingPromise = streamAudioFileFromOffset();
}

function handleStopStreaming() {
  modulePaused = true;
  if (moduleDelay) moduleDelay.cancel();
  stopModulePlayback();
  websocketService.stop();

  const sessionId = websocketService.getSessionId() || store.getState().session.currentSession?.session_id;
  if (sessionId) {
    store.dispatch(fetchSession(sessionId) as any);
    store.dispatch(fetchTranscript(sessionId) as any);
  }

  store.dispatch(stopFileStreaming());
  store.dispatch(enableNoteRecording());
  modulePcmData = null;
  moduleOffset = 0;
  moduleTotalBytes = 0;
}

const AudioFileUpload: React.FC<AudioFileUploadProps> = ({
  size = 'medium',
  useCaseType,
  bilingualMode = false,
  siteCode,
  disabled = false,
  onBeforeStart,
}) => {
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [playAudio, setPlayAudio] = useState(modulePlayAudio);
  const [hasUploaded, setHasUploaded] = useState(false);

  const { isRecording, isNoteRecording, isFileStreaming, isFileStreamingPaused, fileStreamingProgress } =
    useAppSelector((state) => state.recording);
  const isAdmin = useAppSelector((state) => state.user.isAdmin);

  if (!isAdmin) return null;

  const canUpload = !isRecording && !isNoteRecording && !isFileStreaming && !hasUploaded && !disabled;

  const handlePlayToggle = () => {
    const newValue = !playAudio;
    setPlayAudio(newValue);
    modulePlayAudio = newValue;
    if (newValue && isFileStreaming && !isFileStreamingPaused && modulePcmData) {
      startPlayback(modulePcmData, moduleSampleRate, moduleOffset);
    } else if (!newValue) {
      stopModulePlayback();
    }
  };

  const handleStop = () => {
    handleStopStreaming();
    setHasUploaded(true);
  };

  const handleFileSelect = () => {
    if (!canUpload) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';

    try {
      const buffer = await file.arrayBuffer();

      let pcmData: ArrayBuffer;
      let sampleRate = 16000;

      if (file.name.toLowerCase().endsWith('.wav')) {
        const header = parseWavHeader(buffer);
        if (!header) {
          dispatch(showError('Invalid WAV file'));
          return;
        }
        if (header.bitsPerSample !== 16) {
          dispatch(showError('Only 16-bit WAV files are supported'));
          return;
        }
        sampleRate = header.sampleRate;
        pcmData = buffer.slice(header.dataOffset);
      } else {
        pcmData = buffer;
      }

      const languageCode = bilingualMode ? LANGUAGE_OPTIONS_BILINGUAL_EN_ES : undefined;
      onBeforeStart?.();
      startStreaming(pcmData, sampleRate, useCaseType, languageCode, siteCode);
    } catch (error: any) {
      console.error('Failed to process audio file:', error);
      dispatch(showError(error.message || 'Failed to process audio file'));
    }
  };

  // Hide entirely if upload was already used this session
  if (hasUploaded && !isFileStreaming) return null;

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pcm,.wav,.raw"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      {isFileStreaming ? (
        <>
          {isFileStreamingPaused ? (
            <Button onClick={handleResumeStreaming} size={size} type="icon">
              <Icon tokens={playTokens} />
            </Button>
          ) : (
            <Button onClick={handlePauseStreaming} size={size} type="icon">
              <Icon tokens={pauseTokens} />
            </Button>
          )}
          <Button onClick={handleStop} size={size} type="icon">
            <Icon tokens={stopTokens} />
          </Button>
          <div style={{
            width: '120px',
            height: '6px',
            backgroundColor: '#e0e0e0',
            borderRadius: '3px',
            overflow: 'hidden',
          }}>
            <div style={{
              width: `${fileStreamingProgress}%`,
              height: '100%',
              backgroundColor: isFileStreamingPaused ? '#f59e0b' : '#0073bb',
              borderRadius: '3px',
              transition: 'width 0.3s ease',
            }} />
          </div>
          <span style={{ fontSize: '11px', color: TEXT_COLOR_SECONDARY, minWidth: '32px' }}>
            {fileStreamingProgress}%
          </span>
          <Button onClick={handlePlayToggle} size={size} type="icon">
            <Icon tokens={playAudio ? soundOnTokens : soundOffTokens} />
          </Button>
        </>
      ) : (
        <Button onClick={handleFileSelect} size={size} type="icon" disabled={!canUpload}>
          <Icon tokens={uploadSmallTokens} />
        </Button>
      )}
    </div>
  );
};

export default AudioFileUpload;
